import React, { useRef, useState, useEffect } from "react";
import useAddStaff from "./hooks/useAddStaff";
import Modal from "../../common-components/Modal";
import { useTranslation } from 'react-i18next';
import SecondaryButton from "../../common-components/Buttons/SecondaryButton";
import TertiaryButton from "../../common-components/Buttons/TertiaryButton";
import PrimaryButton from "../../common-components/Buttons/PrimaryButton";
import Webcam from "react-webcam";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleUser, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FaCamera } from "react-icons/fa6";
import { FaCloudUploadAlt } from "react-icons/fa";
import useGetDoctorList from "../hip/dashboard/create-appointment/hooks/useGetDoctorList";

const AddStaff = ({ showModal, setIsStaffModalOpen, isEditable = false, currentItem = {}, getAllUsers }) => {
  const { t } = useTranslation();
  const [staffInfo, setStaffInfo] = useState({
    staffName: "",
    email: "",
    mobileNumber: "",
    photo: "",
    age: 25,
    username: "",
    password: "",
    confirmPassword: "",
    userType: "ASSISTANT",
    headId: "",
  });

  const [password, setPassword] = useState({
    password: true,
    confirmPassword: true,
  });

  const fileInputRef = useRef(null);
  const webcamRef = useRef(null);
  const [isWebcamOpen, setIsWebcamOpen] = useState(false);

  // Get doctor list
  const { getDoctorList, doctorList, isDoctorListLoading } = useGetDoctorList();

  useEffect(() => {
    getDoctorList();
  }, [getDoctorList]);

  useEffect(() => {
    if (isEditable && currentItem) {
      setStaffInfo({
        staffName: currentItem.name || "",
        email: currentItem.email || "",
        mobileNumber: currentItem.mobileNumber || "",
        photo: currentItem.photo || "",
        age: currentItem.age || 25,
        username: currentItem.username || "",
        password: "",
        confirmPassword: "",
        userType: "ASSISTANT",
        headId: currentItem.headId || "",
      });
    }
  }, [isEditable, currentItem]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setStaffInfo({
      ...staffInfo,
      [name]: name === "age" ? Number(value) : value,
    });
  };

  const handleDoctorChange = (e) => {
    const selectedDoctor = doctorList.find(doctor => doctor.id === e.target.value);
    setStaffInfo({
      ...staffInfo,
      headId: selectedDoctor ? selectedDoctor.id : "",
    });
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setStaffInfo((prev) => ({
          ...prev,
          photo: reader.result.split(",")[1],
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const toggleWebcam = () => {
    setIsWebcamOpen(!isWebcamOpen);
  };

  const capture = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      setStaffInfo((prev) => ({
        ...prev,
        photo: imageSrc.split(",")[1],
      }));
      setIsWebcamOpen(false);
    }
  };

  const isFormValid = () => {
    const { staffName, mobileNumber, username, headId } = staffInfo;
    if (isEditable) {
      return staffName && mobileNumber && username && headId;
    }
    return staffName && mobileNumber && username && staffInfo.password && staffInfo.password === staffInfo.confirmPassword && headId;
  };

  const onReset = () => {
    if (isEditable) {
      setStaffInfo({
        staffName: currentItem.name || "",
        email: currentItem.email || "",
        mobileNumber: currentItem.mobileNumber || "",
        photo: currentItem.photo || "",
        age: currentItem.age || 25,
        username: currentItem.username || "",
        password: "",
        confirmPassword: "",
        userType: "ASSISTANT",
        headId: currentItem.headId || "",
      });
    } else {
      setStaffInfo({
        staffName: "",
        email: "",
        mobileNumber: "",
        photo: "",
        age: 25,
        username: "",
        password: "",
        confirmPassword: "",
        userType: "ASSISTANT",
        headId: "",
      });
    }
    setIsWebcamOpen(false);
  };

  const onCloseModal = () => {
    setIsStaffModalOpen(false);
    onReset();
    setIsWebcamOpen(false);
  };

  const { isAddStaffLoading, onAddNewStaff } = useAddStaff({
    staffInfo,
    isEditable,
    currentItem,
  });

  return (
    <Modal
      showModal={showModal}
      modalWidth="w-[80%]"
      modalHeight="h-[90%]"
      onClose={onCloseModal}
    >
      <div className="p-5 relative">
        <div className="flex mb-5 relative flex-col items-center justify-center">
          <div className="w-20 h-20 rounded-full border border-gray-300 flex items-center justify-center overflow-hidden">
            {isWebcamOpen && (
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                className="object-cover rounded-full w-20 h-20"
              />
            )}
            {staffInfo.photo && !isWebcamOpen && (
              <img
                src={`data:image/jpeg;base64,${staffInfo.photo}`}
                alt="Profile Preview"
                className="w-full h-full object-cover"
              />
            )}
            {!isWebcamOpen && !staffInfo.photo && (
              <FontAwesomeIcon icon={faCircleUser} size="5x" color="#2323" />
            )}
          </div>
        </div>

        <div className="flex w-full my-3 justify-between items-center">
          <button
            onClick={isWebcamOpen ? capture : toggleWebcam}
            className="flex active:animate-bounce gap-2 hover:border-2 w-[45%] h-9 border disabled:bg-[#F9FAFB] items-center justify-center bg-white border-[#C6C7C9] item-center rounded-lg text-sm text-[#1F2A37]"
          >
            <FaCamera />
            {isWebcamOpen ? `${t("Click Photo")}` : `${t("Open Camera")}`}
          </button>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleImageUpload}
            className="hidden"
          />
          <button
            onClick={handleImageClick}
            className="flex active:animate-bounce gap-2 w-[45%] hover:border-2 h-9 border disabled:bg-[#F9FAFB] items-center justify-center bg-white border-[#C6C7C9] item-center py-2 rounded-lg text-sm text-[#1F2A37]"
          >
            <FaCloudUploadAlt />
            {t("Upload Photo")}
          </button>
        </div>

        <div className="grid gap-5 grid-cols-3">
          <div className="my-2">
            <div className="text-[#5E6066] mb-2.5">{t("Staff Name")}*</div>
            <div className="flex">
              <input
                type="text"
                name="staffName"
                value={staffInfo.staffName}
                onChange={handleChange}
                placeholder={t("Enter staff name")}
                className="border w-full bg-[#F9FAFB] focus:border-[#2D2E33] text-sm text-gray-800 py-2 focus:outline-none rounded-lg placeholder:text-[#D1D5DB] px-3"
                required
              />
            </div>
          </div>
          <div className="my-2">
            <div className="text-[#5E6066] mb-2.5">{t("Age")}</div>
            <div className="flex">
              <input
                type="text"
                name="age"
                min={0}
                value={staffInfo.age}
                onChange={handleChange}
                placeholder={t("Age")}
                className="border w-full bg-[#F9FAFB] focus:border-[#2D2E33] text-sm text-gray-800 py-2 focus:outline-none rounded-lg placeholder:text-[#D1D5DB] px-3"
              />
            </div>
          </div>
          <div className="my-2">
            <div className="text-[#5E6066] mb-2.5">{t("Mobile Number")}*</div>
            <div className="flex">
              <input
                type="tel"
                name="mobileNumber"
                value={staffInfo.mobileNumber}
                onChange={handleChange}
                placeholder={t("Enter mobile number")}
                className="border w-full bg-[#F9FAFB] focus:border-[#2D2E33] text-sm text-gray-800 py-2 focus:outline-none rounded-lg placeholder:text-[#D1D5DB] px-3"
                required
              />
            </div>
          </div>
          <div className="my-2">
            <div className="text-[#5E6066] mb-2.5">{t("Email")}</div>
            <div className="flex">
              <input
                type="email"
                name="email"
                value={staffInfo.email}
                onChange={handleChange}
                placeholder={t("Enter email address")}
                className="border w-full bg-[#F9FAFB] focus:border-[#2D2E33] text-sm text-gray-800 py-2 focus:outline-none rounded-lg placeholder:text-[#D1D5DB] px-3"
              />
            </div>
          </div>
          <div className="my-2">
            <div className="text-[#5E6066] mb-2.5">{t("Username")}*</div>
            <div className="flex">
              <input
                type="text"
                name="username"
                value={staffInfo.username}
                onChange={handleChange}
                placeholder={t("Enter username")}
                className="border w-full bg-[#F9FAFB] focus:border-[#2D2E33] text-sm text-gray-800 py-2 focus:outline-none rounded-lg placeholder:text-[#D1D5DB] px-3"
                required
              />
            </div>
          </div>
          <div className="my-2">
            <div className="text-[#5E6066] mb-2.5">{t("Select Doctor")}*</div>
            <div className="flex">
              <select
                name="headId"
                value={staffInfo.headId}
                onChange={handleDoctorChange}
                className="border w-full bg-[#F9FAFB] focus:border-[#2D2E33] text-sm text-gray-800 py-2 focus:outline-none rounded-lg placeholder:text-[#D1D5DB] px-3"
                required
                disabled={isDoctorListLoading}
              >
                <option value="">{t("Select a doctor")}</option>
                {doctorList.map((doctor) => (
                  <option key={doctor.id} value={doctor.id}>
                    {doctor.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="my-2 relative">
            <div className="text-[#5E6066] mb-2.5">{t("Password")}{!isEditable && "*"}</div>
            <div className="flex">
              <input
                type={password.password ? "password" : "text"}
                name="password"
                value={staffInfo.password}
                onChange={handleChange}
                placeholder={t("Enter password")}
                className="border w-full bg-[#F9FAFB] focus:border-[#2D2E33] text-sm text-gray-800 py-2 focus:outline-none rounded-lg placeholder:text-[#D1D5DB] px-3"
                required={!isEditable}
              />
              <button
                onClick={(e) => {
                  e.preventDefault();
                  setPassword((prev) => ({
                    ...prev,
                    password: !prev.password,
                  }));
                }}
                className="px-2 absolute top-10 right-0"
              >
                <FontAwesomeIcon
                  color="#6B7280"
                  icon={password.password ? faEyeSlash : faEye}
                />
              </button>
            </div>
          </div>
          <div className="my-2 relative">
            <div className="text-[#5E6066] mb-2.5">{t("Confirm Password")}{!isEditable && "*"}</div>
            <div className="flex">
              <input
                type={password.confirmPassword ? "password" : "text"}
                name="confirmPassword"
                value={staffInfo.confirmPassword}
                onChange={handleChange}
                placeholder={t("Confirm password")}
                className="border w-full bg-[#F9FAFB] focus:border-[#2D2E33] text-sm text-gray-800 py-2 focus:outline-none rounded-lg placeholder:text-[#D1D5DB] px-3"
                required={!isEditable}
              />
              {staffInfo.password !== staffInfo.confirmPassword && staffInfo.confirmPassword && (
                <p className="text-[#E02424] font-normal text-xs m-1">
                  Password and Confirm Password do not match
                </p>
              )}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  setPassword((prev) => ({
                    ...prev,
                    confirmPassword: !prev.confirmPassword,
                  }));
                }}
                className="px-2 absolute top-10 right-0"
              >
                <FontAwesomeIcon
                  color="#6B7280"
                  icon={password.confirmPassword ? faEyeSlash : faEye}
                />
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-5 mt-5 justify-end">
          <TertiaryButton
            buttonName={t("Reset")}
            width="w-fit"
            onClick={onReset}
          />
          <PrimaryButton
            buttonName={t(isEditable ? "Update" : "Save")}
            width="w-fit"
            onClick={() => {
              onAddNewStaff()
                .then(() => {
                  getAllUsers();
                  onCloseModal();
                })
                .catch(() => {
                  // Error already handled in the hook
                });
            }}
            disabled={isAddStaffLoading || !isFormValid()}
          />
        </div>
      </div>
    </Modal>
  );
};

export default AddStaff; 